/**
 * CeremonySection -- 예식 정보 섹션 편집
 */

import { useState } from 'react'
import { Calendar, CalendarDays, Building2, Phone, MapPin, Bus, CarFront, Search } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { apiFetch } from '../../../lib/api.js'
import { Button, DateTimePicker, Input, RadioGroup, Textarea } from '../../ui/index.js'
import styles from './SectionForm.module.css'

interface PlaceResult {
  name: string
  address: string
  roadAddress: string
  lat: number
  lng: number
}

const CALENDAR_STYLE_OPTIONS = [
  { value: 'calendar', label: '달력' },
  { value: 'countdown', label: '카운트다운' },
  { value: 'minimal', label: '미니멀' },
  { value: 'none', label: '표시 안함' },
]

export function CeremonySection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<PlaceResult[]>([])

  if (!invitation) return null

  const wedding = invitation.wedding
  const venue = wedding?.venue
  const hasCoords = typeof venue?.lat === 'number' && typeof venue?.lng === 'number'

  const handleSearch = async () => {
    const query = venue?.address?.trim()
    if (!query || isSearching) return

    setIsSearching(true)
    setSearchError(null)
    setSearchResults([])

    const res = await apiFetch<PlaceResult[]>(`/api/maps/search?query=${encodeURIComponent(query)}`, { skipAuth: true })

    setIsSearching(false)

    if (!res.success) {
      setSearchError('검색에 실패했습니다')
      return
    }

    if (res.data.length === 0) {
      setSearchError('검색 결과가 없습니다')
      return
    }

    if (res.data.length === 1) {
      handleSelectResult(res.data[0])
    } else {
      setSearchResults(res.data)
    }
  }

  const handleSelectResult = (result: PlaceResult) => {
    updateNested('wedding.venue.address', result.roadAddress || result.address)
    updateNested('wedding.venue.lat', result.lat)
    updateNested('wedding.venue.lng', result.lng)
    setSearchResults([])
  }

  const handleAddressKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
  }

  return (
    <div className={styles.form}>
      {/* 예식 일시 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <Calendar size={16} /> 예식 일시
        </span>
        <DateTimePicker
          value={wedding?.date ?? null}
          onChange={(_date, iso) => updateNested('wedding.date', iso)}
        />
      </div>

      {/* 캘린더 스타일 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <CalendarDays size={16} /> 캘린더 스타일
        </span>
        <RadioGroup
          name="calendarStyle"
          value={invitation.settings?.calendarStyle ?? 'calendar'}
          onChange={(value) => updateNested('settings.calendarStyle', value)}
          options={CALENDAR_STYLE_OPTIONS}
        />
      </div>

      {/* 예식장 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <Building2 size={16} /> 예식장
        </span>
        <div className={styles.row}>
          <Input
            label="예식장 이름"
            value={venue?.name ?? ''}
            onChange={(e) => updateNested('wedding.venue.name', e.target.value)}
            placeholder="그랜드 웨딩홀"
          />
          <Input
            label="홀"
            value={venue?.hall ?? ''}
            onChange={(e) => updateNested('wedding.venue.hall', e.target.value)}
            placeholder="3층 그랜드볼룸"
          />
        </div>
      </div>

      {/* 주소 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <MapPin size={16} /> 주소
        </span>
        <div className={styles.row}>
          <Input
            value={venue?.address ?? ''}
            onChange={(e) => {
              updateNested('wedding.venue.address', e.target.value)
              setSearchResults([])
            }}
            onKeyDown={handleAddressKeyDown}
            placeholder="서울특별시 강남구 테헤란로 152"
          />
          <Button
            variant="secondary"
            size="md"
            onClick={handleSearch}
            disabled={!venue?.address?.trim() || isSearching}
            style={{ flex: 'none' }}
          >
            <Search size={16} />
            {isSearching ? '검색중...' : '검색'}
          </Button>
        </div>
        <div className={styles.dropdownAnchor}>
          {searchResults.length > 0 && (
            <div className={styles.dropdown}>
              {searchResults.map((result, i) => (
                <button
                  key={i}
                  type="button"
                  className={styles.dropdownItem}
                  onClick={() => handleSelectResult(result)}
                >
                  <span className={styles.dropdownRoad}>{result.name}</span>
                  <span className={styles.dropdownJibun}>{result.roadAddress || result.address}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {hasCoords && <p className={styles.hint}>✓ 지도에 표시됩니다</p>}
        {searchError && <p className={styles.hint} style={{ color: 'var(--ds-color-danger)' }}>{searchError}</p>}
      </div>

      {/* 연락처 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <Phone size={16} /> 연락처
        </span>
        <Input
          value={venue?.tel ?? ''}
          onChange={(e) => updateNested('wedding.venue.tel', e.target.value)}
          placeholder="02-0000-0000"
          type="tel"
        />
      </div>

      {/* 교통편 안내 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <Bus size={16} /> 교통편 안내
        </span>
        <Textarea
          value={venue?.transportation ?? ''}
          onChange={(e) => updateNested('wedding.venue.transportation', e.target.value)}
          placeholder="지하철: 2호선 역삼역 3번 출구&#10;버스: 146, 341, 360"
          rows={3}
        />
      </div>

      {/* 주차 안내 */}
      <div className={styles.subSection}>
        <span className={styles.subSectionLabel}>
          <CarFront size={16} /> 주차 안내
        </span>
        <Textarea
          value={venue?.parking ?? ''}
          onChange={(e) => updateNested('wedding.venue.parking', e.target.value)}
          placeholder="건물 지하 2~3층 주차 가능&#10;2시간 무료 (프론트 도장)"
          rows={2}
        />
      </div>
    </div>
  )
}
